import { Button } from "@/components/ui/button";
import { ExternalLink, Globe, ShieldCheck } from "lucide-react";

/**
 * Panel lateral para portales gubernamentales (SAT, IMSS, IDSE).
 * No embebe iframe: el SAT/IMSS bloquean X-Frame-Options; el usuario abre pestaña externa.
 */
export default function PortalExternoGuiaPanel({
  title,
  portalUrl,
  badge = "Consulta en portal oficial",
  intro,
  steps = [],
  footerNote,
  openButtonLabel = "Abrir portal en nueva pestaña",
  testId = "btn-abrir-portal-externo",
}) {
  return (
    <div
      className="flex flex-col min-h-[420px] xl:min-h-0 bg-slate-100"
      data-testid="portal-externo-guia-panel"
    >
      <div className="px-3 py-2 bg-[#0F2D5C] text-white text-xs shrink-0">
        <span className="font-semibold truncate block">{title}</span>
      </div>

      <div className="flex-1 overflow-y-auto p-4 md:p-6 space-y-4">
        <div className="rounded-md border border-amber-200 bg-amber-50/90 px-3 py-3 text-xs text-amber-950 leading-relaxed">
          <p className="m-0 font-semibold mb-1">¿Por qué no se muestra el portal aquí?</p>
          <p className="m-0">
            El SAT y el IMSS no permiten embeber sus páginas en otras aplicaciones (seguridad anti-fraude).
            Use el botón inferior para abrir el sitio oficial en su navegador, resolver captcha e iniciar sesión si aplica.
          </p>
        </div>

        <div className="bg-white rounded-md border border-slate-200 p-4 space-y-4">
          <div className="flex items-center gap-2 text-[#0F2D5C]">
            <Globe className="w-5 h-5 shrink-0" />
            <span className="text-xs font-semibold uppercase tracking-wide">{badge}</span>
          </div>

          {intro ? (
            <p className="text-xs text-slate-600 leading-relaxed m-0">{intro}</p>
          ) : null}

          {steps.length ? (
            <ol className="space-y-3 m-0 pl-0 list-none">
              {steps.map((step, idx) => (
                <li key={step.key || idx} className="flex gap-3 text-xs">
                  <span className="inline-flex w-6 h-6 shrink-0 items-center justify-center rounded-full bg-[#0F2D5C] text-white text-[10px] font-bold">
                    {idx + 1}
                  </span>
                  <div>
                    <p className="font-semibold text-slate-800 m-0">{step.title}</p>
                    {step.description ? (
                      <p className="text-slate-600 m-0 mt-0.5 leading-relaxed">{step.description}</p>
                    ) : null}
                  </div>
                </li>
              ))}
            </ol>
          ) : null}

          <Button
            type="button"
            asChild
            className="w-full h-12 font-bold bg-emerald-700 hover:bg-emerald-800"
            data-testid={testId}
          >
            <a href={portalUrl} target="_blank" rel="noopener noreferrer">
              <ExternalLink className="w-4 h-4 mr-2" />
              {openButtonLabel}
            </a>
          </Button>

          <p className="text-[10px] text-slate-500 m-0 font-mono break-all">{portalUrl}</p>
        </div>

        <div className="rounded-md border border-sky-200 bg-sky-50/80 px-3 py-2 text-[11px] text-sky-900 flex gap-2">
          <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
          <p className="m-0 leading-relaxed">
            Visor Fiscal no envía contraseñas al portal. Trabaje en el navegador oficial y regrese aquí
            para procesar archivos o marcar estatus.
          </p>
        </div>
      </div>

      {footerNote ? (
        <p className="text-[10px] text-slate-500 px-3 py-2 bg-slate-50 border-t m-0">{footerNote}</p>
      ) : null}
    </div>
  );
}
